/**
 * EditorState - Centralized reactive state management using EventTarget
 * 
 * This class manages all application state and emits events when state changes.
 * Components subscribe to state changes via event listeners.
 * 
 * Events emitted:
 * - 'stateChanged' - Any state change (detail: { changes })
 * - '<key>' - Specific property change (detail: { value, allChanges })
 * - 'sliderChanged' - Single slider changed (detail: { name, value })
 * - 'sliderReleased' - Slider mouseup/release (detail: { sliderName })
 * - 'slidersLoaded' - All sliders loaded (detail: { sliders })
 * - 'photoChanged' - Current photo changed (detail: { photoId, oldPhotoId })
 * - 'isSliderDragging' - Slider drag state changed (detail: { value, oldValue })
 */

import { SLIDER_CONFIGS } from './slider_mappings.js';

export class EditorState extends EventTarget {
  constructor() {
    super();
    
    // Initialize default slider values
    const defaultSliders = {};
    SLIDER_CONFIGS.forEach(config => {
      defaultSliders[config.name] = config.default;
    });
    
    this._state = {
      // Navigation
      currentPage: 'photos',
      currentFolderId: null,
      activePhotoId: null,
      selectedPhotoIds: new Set(),
      
      // Current photo's slider values (loaded when photo changes)
      sliders: { ...defaultSliders },
      
      // Global render settings
      inferenceSteps: 6,
      fullResInferenceSteps: 28,
      fullResolutionSize: 1024,
      seed: 42,
      baseShift: 0.5,
      maxShift: 1.15,
      sampleEvery: 2,
      
      // Preview settings
      previewMode: 'tooltip',
      
      // View mode for Edit page
      viewMode: 'normal', // 'normal', 'sidebyside', 'split'
      splitPosition: 50, // Split view slider position (0-100%)
      
      // Style selector
      stylePrompt: null,
      
      // Upload progress
      uploadProgress: {},
      
      // Slider interaction tracking
      isSliderDragging: false,  // True when any slider is being actively dragged
      
      // Export queue
      exportQueueItems: [],  // Array of export queue items from server
      exportSaveLocation: 'zip',  // 'original'|'folder'|'download_all'|'zip'
      exportFilenamePattern: '{filename}_tg',  // Filename pattern with variables
      exportTargetFolderHandle: null,  // FileSystemDirectoryHandle for "Download to Folder"
      activeExports: new Map(),  // Map of photo_id → { status, progress, error }
      isExporting: false  // True when export process is running
    };
    
    // Batch updates to avoid multiple events
    this._batchDepth = 0;
    this._pendingChanges = {};
    this._pendingPhotoChangeEvent = null;
  }
  
  /**
   * Get a specific state value or all state
   */
  get(key) {
    if (key === undefined) {
      // Return state as-is (keeping Set intact for internal use)
      return { ...this._state };
    }
    return this._state[key];
  }
  
  /**
   * Set one or more state values
   * @param {object} updates - Object with key-value pairs to update
   */
  set(updates) {
    console.log('[EditorState] >>> set() called with keys:', Object.keys(updates));
    const changes = {};
    
    for (const [key, value] of Object.entries(updates)) {
      if (this._state[key] !== value) {
        const oldValue = this._state[key];
        this._state[key] = value;
        changes[key] = { value, oldValue };
        console.log(`[EditorState] Changed: ${key}`);
      }
    }
    
    if (Object.keys(changes).length === 0) {
      console.log('[EditorState] No changes detected');
      return;
    }
    
    // If batching, accumulate changes
    if (this._batchDepth > 0) {
      console.log('[EditorState] Batching - accumulating changes');
      Object.assign(this._pendingChanges, changes);
      return;
    }
    
    console.log('[EditorState] Emitting changes:', Object.keys(changes));
    this._emitChanges(changes);
  }
  
  /**
   * Batch multiple updates into a single event emission
   */
  batch(fn) {
    this._batchDepth++;
    try {
      fn();
    } finally {
      this._batchDepth--;
      
      if (this._batchDepth === 0) {
        // Emit pending state changes
        if (Object.keys(this._pendingChanges).length > 0) {
          this._emitChanges(this._pendingChanges);
          this._pendingChanges = {};
        }
        
        // Emit pending photoChanged event after all changes
        if (this._pendingPhotoChangeEvent) {
          const eventData = this._pendingPhotoChangeEvent;
          this._pendingPhotoChangeEvent = null;
          this.dispatchEvent(new CustomEvent('photoChanged', {
            detail: eventData
          }));
        }
      }
    }
  }
  
  /**
   * Emit change events
   */
  _emitChanges(changes) {
    // Emit specific events for each change
    for (const [key, { value, oldValue }] of Object.entries(changes)) {
      this.dispatchEvent(new CustomEvent(key, { 
        detail: { value, oldValue, allChanges: changes } 
      }));
    }
    
    // Emit general change event
    this.dispatchEvent(new CustomEvent('stateChanged', { 
      detail: { changes } 
    }));
  }
  
  /**
   * Set a single slider value
   */
  setSlider(name, value) {
    if (this._state.sliders[name] === value) return;
    
    const oldValue = this._state.sliders[name];
    this._state.sliders = { ...this._state.sliders, [name]: value };
    
    this.dispatchEvent(new CustomEvent('sliderChanged', {
      detail: { name, value, oldValue, allSliders: this._state.sliders }
    }));
  }
  
  /**
   * Get a single slider value
   */
  getSlider(name) {
    return this._state.sliders[name];
  }
  
  /**
   * Get all slider values
   */
  getAllSliders() {
    return { ...this._state.sliders };
  }
  
  /**
   * Load all slider values at once (when switching photos)
   * @param {object} sliders - Object with slider name-value pairs
   */
  loadSliders(sliders) {
    const oldSliders = this._state.sliders;
    this._state.sliders = { ...sliders };
    
    this.dispatchEvent(new CustomEvent('slidersLoaded', {
      detail: { sliders: this._state.sliders, oldSliders }
    }));
  }
  
  /**
   * Reset all sliders to default values
   */
  resetSliders() {
    const defaults = {};
    SLIDER_CONFIGS.forEach(config => {
      defaults[config.name] = config.default;
    });
    this.loadSliders(defaults);
  }
  
  /**
   * Set active photo and load its sliders
   * @param {number|null} photoId - Photo ID
   * @param {object} photo - Full photo object with slider values
   */
  setActivePhoto(photoId, photo = null) {
    const oldPhotoId = this._state.activePhotoId;
    
    if (oldPhotoId === photoId) return;
    
    this.batch(() => {
      // Build updates object to use set() for proper change tracking
      const updates = { activePhotoId: photoId };
      
      // Load slider values from photo if provided
      if (photo) {
        const sliders = {};
        SLIDER_CONFIGS.forEach(config => {
          sliders[config.name] = photo[config.name] ?? config.default;
        });
        this._state.sliders = sliders;
        
        // Note: We do NOT load inference_steps or seed from the photo.
        // These are global settings controlled by the Account menu, not per-photo settings.
      }
      
      // Use set() to properly track changes and emit events
      this.set(updates);
      
      // Store photo change event to emit after all batches complete
      this._pendingPhotoChangeEvent = { photoId, oldPhotoId, photo };
    });
    
    // Emit photoChanged event only if we're not in an outer batch
    if (this._batchDepth === 0 && this._pendingPhotoChangeEvent) {
      const eventData = this._pendingPhotoChangeEvent;
      this._pendingPhotoChangeEvent = null;
      this.dispatchEvent(new CustomEvent('photoChanged', {
        detail: eventData
      }));
    }
  }
  
  /**
   * Toggle photo selection
   */
  togglePhotoSelection(photoId) {
    const selectedPhotoIds = new Set(this._state.selectedPhotoIds);
    
    if (selectedPhotoIds.has(photoId)) {
      selectedPhotoIds.delete(photoId);
    } else {
      selectedPhotoIds.add(photoId);
    }
    
    this.set({ selectedPhotoIds });
  }
  
  /**
   * Clear all photo selections
   */
  clearPhotoSelection() {
    this.set({ selectedPhotoIds: new Set() });
  }
  
  /**
   * Select a range of photos
   */
  selectPhotoRange(photoIds) {
    const selectedPhotoIds = new Set(photoIds);
    this.set({ selectedPhotoIds });
  }
  
  /**
   * Add photos to selection
   */
  addToPhotoSelection(photoIds) {
    const selectedPhotoIds = new Set(this._state.selectedPhotoIds);
    photoIds.forEach(id => selectedPhotoIds.add(id));
    this.set({ selectedPhotoIds });
  }
  
  /**
   * Get active photo ID
   */
  getActivePhotoId() {
    return this._state.activePhotoId;
  }
  
  /**
   * Set slider dragging state
   */
  setSliderDragging(isDragging) {
    if (this._state.isSliderDragging !== isDragging) {
      this.set({ isSliderDragging: isDragging });
    }
  }
  
  /**
   * Check if any slider is currently being dragged
   */
  isSliderBeingDragged() {
    return this._state.isSliderDragging;
  }
  
  /**
   * Get serializable state for persistence (IndexedDB)
   */
  getSerializableState() {
    return {
      currentPage: this._state.currentPage,
      currentFolderId: this._state.currentFolderId,
      activePhotoId: this._state.activePhotoId,
      selectedPhotoIds: Array.from(this._state.selectedPhotoIds),
      inferenceSteps: this._state.inferenceSteps,
      fullResInferenceSteps: this._state.fullResInferenceSteps,
      fullResolutionSize: this._state.fullResolutionSize,
      seed: this._state.seed,
      baseShift: this._state.baseShift,
      maxShift: this._state.maxShift,
      sampleEvery: this._state.sampleEvery,
      previewMode: this._state.previewMode,
      viewMode: this._state.viewMode,
      stylePrompt: this._state.stylePrompt
    };
  }
  
  /**
   * Load state from serialized data (IndexedDB)
   */
  loadSerializedState(savedState) {
    this.batch(() => {
      if (savedState.currentPage) this._state.currentPage = savedState.currentPage;
      // Support both currentFolderId (new) and currentFolder (old - extract id)
      if (savedState.currentFolderId !== undefined) this._state.currentFolderId = savedState.currentFolderId;
      else if (savedState.currentFolder?.id !== undefined) this._state.currentFolderId = savedState.currentFolder.id;
      // Support old currentPhotoId for backwards compatibility
      if (savedState.activePhotoId !== undefined) this._state.activePhotoId = savedState.activePhotoId;
      else if (savedState.currentPhotoId !== undefined) this._state.activePhotoId = savedState.currentPhotoId;
      if (savedState.selectedPhotoIds) this._state.selectedPhotoIds = new Set(savedState.selectedPhotoIds);
      if (savedState.inferenceSteps) this._state.inferenceSteps = savedState.inferenceSteps;
      if (savedState.fullResInferenceSteps) this._state.fullResInferenceSteps = savedState.fullResInferenceSteps;
      if (savedState.fullResolutionSize) this._state.fullResolutionSize = savedState.fullResolutionSize;
      if (savedState.seed !== undefined) this._state.seed = savedState.seed;
      if (savedState.baseShift !== undefined) this._state.baseShift = savedState.baseShift;
      if (savedState.maxShift !== undefined) this._state.maxShift = savedState.maxShift;
      if (savedState.sampleEvery !== undefined) this._state.sampleEvery = savedState.sampleEvery;
      if (savedState.previewMode) this._state.previewMode = savedState.previewMode;
      if (savedState.viewMode) this._state.viewMode = savedState.viewMode;
      if (savedState.stylePrompt !== undefined) this._state.stylePrompt = savedState.stylePrompt;
    });
  }
}

